/**
 * Contact Edit Slide-Over — Academic Curator
 * Slide-over panel for adding/editing contacts with type selector and live preview.
 */

import { useState, useEffect } from 'react';
import { X, Shield } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useAppAuth } from '@/lib/mock-auth';
import { graphqlRequest } from '@/lib/graphql-client';
import { CREATE_CONTACT, UPDATE_CONTACT } from '../graphql/mutations';
import { CONTACT_TYPE_NAMES } from '@/lib/contact-types';
import { validateContactForm } from '@/lib/contact-validation';

const ADDRESS_TYPE_IDS = new Set([6, 13, 17, 18]);

export interface EditingContact {
  id: string;
  typeId: number;
  contactValue: { val?: string; formattedAddress?: string };
  isActive: boolean;
  isRecovery: boolean;
}

export interface ExistingContactSummary {
  typeId: number;
  rank: number;
}

interface ContactSlideOverProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  contact?: EditingContact;
  existingContacts?: ExistingContactSummary[];
}

/** Determine if a typeId is an email-like type (for input type hint) */
function isEmailType(typeId: number): boolean {
  return [1, 14].includes(typeId);
}

/** Determine if a typeId is a phone-like type (for input type hint) */
function isPhoneType(typeId: number): boolean {
  return [2, 3, 4, 5, 15, 16].includes(typeId);
}

function getPlaceholder(typeId: number): string {
  if (isEmailType(typeId)) return 'jane@university.edu';
  if (isPhoneType(typeId)) return '+1 (555) 012-3456';
  if (ADDRESS_TYPE_IDS.has(typeId)) return '';
  if ([7, 8, 9, 10].includes(typeId)) return 'username';
  if (typeId === 11) return 'https://example.com';
  if (typeId === 12) return 'Contact name & number';
  if (typeId === 19) return 'Homestay code';
  return 'Enter value';
}

function getInputType(typeId: number): string {
  if (isEmailType(typeId)) return 'email';
  if (isPhoneType(typeId)) return 'tel';
  if (typeId === 11) return 'url';
  return 'text';
}

function getValueLabel(typeId: number): string {
  if (isEmailType(typeId)) return 'Email Address';
  if (isPhoneType(typeId)) return 'Phone Number';
  if (typeId === 11) return 'Website URL';
  if (typeId === 12) return 'Emergency Contact Details';
  if (typeId === 19) return 'Homestay Code';
  if ([7, 8, 9, 10].includes(typeId)) return 'Username / Handle';
  return 'Value';
}

export function ContactSlideOver({ open, onClose, onSaved, contact: editContact, existingContacts = [] }: ContactSlideOverProps) {
  const [typeId, setTypeId] = useState<number>(1);
  const [val, setVal] = useState('');
  const [building, setBuilding] = useState('');
  const [unit, setUnit] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [streetName, setStreetName] = useState('');
  const [locality, setLocality] = useState('');
  const [region, setRegion] = useState('');
  const [postcode, setPostcode] = useState('');
  const [country, setCountry] = useState('United States');
  const [activeFrom, setActiveFrom] = useState('');
  const [activeTo, setActiveTo] = useState('');
  const [isRecovery, setIsRecovery] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const auth = useAppAuth();

  const isAddress = ADDRESS_TYPE_IDS.has(typeId);
  const isEditing = !!editContact;
  const showRecovery = isEmailType(typeId) || isPhoneType(typeId);

  useEffect(() => {
    if (!open) {
      setVal(''); setBuilding(''); setUnit(''); setStreetNumber('');
      setStreetName(''); setLocality(''); setRegion(''); setPostcode('');
      setCountry('United States'); setActiveFrom(''); setActiveTo('');
      setIsRecovery(false); setSaving(false); setTypeId(1);
      setError(null);
    } else if (editContact) {
      setTypeId(editContact.typeId);
      setIsRecovery(editContact.isRecovery);
      if (ADDRESS_TYPE_IDS.has(editContact.typeId)) {
        setVal('');
      } else {
        setVal(editContact.contactValue.val || '');
      }
    }
  }, [open, editContact]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    // Client-side validation
    const validationErrors = validateContactForm({
      typeId, val, building, unit, streetNumber, streetName,
      locality, region, postcode, country,
    });
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      setSaving(false);
      return;
    }

    try {
      const idToken = auth.user?.id_token;
      if (!idToken) throw new Error('Not authenticated');

      if (isEditing && editContact) {
        // Update existing contact
        const input: Record<string, unknown> = {
          isActive: true,
          isRecovery: isRecovery || undefined,
          activeFrom: activeFrom || undefined,
          activeTo: activeTo || undefined,
        };

        if (isAddress) {
          Object.assign(input, {
            building: building || undefined,
            unit: unit || undefined,
            streetNumber: streetNumber || undefined,
            streetName: streetName || undefined,
            locality: locality || undefined,
            region: region || undefined,
            postcode: postcode || undefined,
            country: country || undefined,
          });
        } else {
          input.val = val;
        }

        await graphqlRequest(UPDATE_CONTACT, { id: editContact.id, input }, idToken);
      } else {
        // Create new contact — backend computes rank automatically
        const userId = auth.user?.profile?.sub ?? '';
        const input: Record<string, unknown> = {
          userId,
          typeId,
          isActive: true,
          isRecovery: isRecovery || undefined,
          activeFrom: activeFrom || undefined,
          activeTo: activeTo || undefined,
        };

        if (isAddress) {
          Object.assign(input, {
            building: building || undefined,
            unit: unit || undefined,
            streetNumber: streetNumber || undefined,
            streetName: streetName || undefined,
            locality: locality || undefined,
            region: region || undefined,
            postcode: postcode || undefined,
            country: country || undefined,
          });
        } else {
          input.val = val;
        }

        await graphqlRequest(CREATE_CONTACT, { input }, idToken);
      }

      onSaved?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : isEditing ? 'Failed to update contact' : 'Failed to create contact');
    } finally {
      setSaving(false);
    }
  };

  const addressPreview = [
    [unit, building].filter(Boolean).join(', '),
    [streetNumber, streetName].filter(Boolean).join(' '),
    locality,
    [region, postcode].filter(Boolean).join(' '),
    country,
  ].filter(Boolean).join(', ');

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-40 animate-fade-in" onClick={onClose} aria-hidden="true" />

      {/* Panel */}
      <div
        className="fixed inset-y-0 right-0 w-full sm:max-w-lg bg-white shadow-2xl z-50 flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
        aria-labelledby="slide-over-title"
      >
        {/* Header */}
        <div className="p-8 pb-4 flex items-center justify-between">
          <div>
            <h2 id="slide-over-title" className="text-2xl font-black text-foreground tracking-tight">
              {isEditing ? 'Edit Contact' : 'Add Contact'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1 italic">
              {isEditing ? 'Updating contact coordinates.' : 'Registering new contact coordinates.'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface-low rounded-full transition-colors" aria-label="Close panel">
            <X className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-8 py-6 space-y-8">
          {/* Contact Type Selector */}
          <div className="space-y-1.5">
            <Label htmlFor="contact-type">Contact Type</Label>
            <select
              id="contact-type"
              value={typeId}
              onChange={(e) => setTypeId(Number(e.target.value))}
              disabled={isEditing}
              className="flex h-10 w-full rounded-xl bg-surface-low px-4 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Contact type"
            >
              {Object.entries(CONTACT_TYPE_NAMES).map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>

          {/* Conditional fields */}
          {!isAddress ? (
            <div className="space-y-1.5">
              <Label htmlFor="contact-value">{getValueLabel(typeId)}</Label>
              <Input
                id="contact-value"
                type={getInputType(typeId)}
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder={getPlaceholder(typeId)}
                required
              />
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="building">Building Name</Label>
                  <Input id="building" value={building} onChange={(e) => setBuilding(e.target.value)} placeholder="e.g. Sterling Heights" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="unit">Unit / Apartment</Label>
                  <Input id="unit" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="e.g. Suite 402" />
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-4 space-y-1.5">
                  <Label htmlFor="street-number">Street No.</Label>
                  <Input id="street-number" value={streetNumber} onChange={(e) => setStreetNumber(e.target.value)} placeholder="128" />
                </div>
                <div className="col-span-8 space-y-1.5">
                  <Label htmlFor="street-name">Street Name</Label>
                  <Input id="street-name" value={streetName} onChange={(e) => setStreetName(e.target.value)} placeholder="Founders Avenue" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="locality">Locality / City</Label>
                  <Input id="locality" value={locality} onChange={(e) => setLocality(e.target.value)} placeholder="Boston" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="region">Region / State</Label>
                  <Input id="region" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Massachusetts" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="postcode">Postcode</Label>
                  <Input id="postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="02108" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="country">Country</Label>
                  <select
                    id="country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="flex h-10 w-full rounded-xl bg-surface-low px-4 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 border-none cursor-pointer"
                  >
                    <option>United States</option>
                    <option>United Kingdom</option>
                    <option>Canada</option>
                    <option>Australia</option>
                  </select>
                </div>
              </div>

              {/* Live preview */}
              {addressPreview && (
                <div className="bg-accent/50 p-6 rounded-2xl space-y-2" style={{ border: '1px solid hsl(243 60% 92%)' }}>
                  <p className="text-[10px] font-black text-primary uppercase tracking-widest flex items-center gap-2">
                    Live Preview
                  </p>
                  <p className="text-sm font-medium text-primary/90 leading-relaxed">{addressPreview}</p>
                </div>
              )}
            </div>
          )}

          {/* Date range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="active-from">Active From</Label>
              <Input id="active-from" type="date" value={activeFrom} onChange={(e) => setActiveFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="active-to">Active To</Label>
              <Input id="active-to" type="date" value={activeTo} onChange={(e) => setActiveTo(e.target.value)} />
            </div>
          </div>

          {/* Recovery toggle */}
          {showRecovery && (
            <div className="flex items-center justify-between p-4 bg-surface-low rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg" aria-hidden="true">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground leading-tight" id="recovery-label">Recovery Contact</p>
                  <p className="text-[11px] text-muted-foreground font-medium" id="recovery-description">Use for emergency or credential recovery</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsRecovery(!isRecovery)}
                className={cn(
                  'w-12 h-6 rounded-full relative p-1 flex items-center transition-all',
                  isRecovery ? 'bg-primary justify-end' : 'bg-surface-high justify-start'
                )}
                role="switch"
                aria-checked={isRecovery}
                aria-labelledby="recovery-label"
                aria-describedby="recovery-description"
              >
                <div className="w-4 h-4 bg-white rounded-full shadow-sm" />
              </button>
            </div>
          )}
        </form>

        {/* Error display */}
        {error && (
          <div className="mx-8 mb-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700" role="alert">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="p-8 pt-4 glass grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-4 rounded-2xl bg-surface-high text-foreground font-bold text-sm hover:bg-surface-highest transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={saving}
            className="px-6 py-4 rounded-2xl bg-gradient-to-r from-primary to-indigo-500 text-white font-bold text-sm shadow-xl shadow-indigo-200/50 flex items-center justify-center gap-3 active:scale-95 transition-all disabled:opacity-70"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" />
                Saving...
              </>
            ) : (
              isEditing ? 'Update Contact' : 'Save Contact'
            )}
          </button>
        </div>
      </div>
    </>
  );
}
